// src/services/adManagement.js
import { db, storage } from '../firebase';
import {
  Timestamp,
  arrayUnion,
  doc,
  getDoc,
  increment,
  serverTimestamp,
  updateDoc,
  runTransaction,
  collection,
  setDoc,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { createNotification } from './NotificationService';

// 1. Mark as Sold
export const markAdAsSold = async (adId, userId) => {
  const adRef = doc(db, 'listings', adId);
  const adSnap = await getDoc(adRef);
  if (!adSnap.exists()) throw new Error('Ad not found');
  const adTitle = adSnap.data().title;

  await updateDoc(adRef, {
    status: 'sold',
    soldAt: serverTimestamp(),
    markedSoldBy: userId,
    updatedAt: serverTimestamp(),
  });

  await createNotification({
    userId: userId,
    type: 'ad_sold',
    title: 'Ad Marked as Sold',
    message: `Congratulations! Your ad "${adTitle}" has been marked as sold.`,
    adId: adId,
    adTitle: adTitle
  });
};

// 2. Delete Ad (Database + Storage Cleanup)
export const deleteAdPermanently = async (adId, photoUrls = []) => {
  try {
    // A. Delete Images from Firebase Storage
    const deletePromises = photoUrls.map((photo) => {
      const urls =
        typeof photo === 'string'
          ? [photo]
          : [photo?.full, photo?.thumb].filter(Boolean);

      return Promise.all(
        urls.map((url) =>
          deleteObject(ref(storage, url)).catch((e) =>
            console.error('Image delete failed:', e)
          )
        )
      );
    });
    await Promise.all(deletePromises);

    // B. Soft-delete document in Firestore to match mobile app behavior
    const adRef = doc(db, 'listings', adId);
    const adSnap = await getDoc(adRef);
    if (!adSnap.exists()) throw new Error('Ad not found');
    const data = adSnap.data();
    const userId = data.userId;
    const adTitle = data.title;

    await updateDoc(adRef, {
      status: 'deleted',
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    if (userId) {
      await createNotification({
        userId: userId,
        type: 'ad_deleted',
        title: 'Ad Deleted',
        message: `Your ad "${adTitle}" has been deleted.`,
        adId: adId,
        adTitle: adTitle
      });
    }
    return true;
  } catch (error) {
    console.error('Delete Error:', error);
    throw error;
  }
};

export const republishAd = async ({
  adId,
  userId,
  additionalDays = 30,
  isPaidRenewal = false,
}) => {
  const adRef = doc(db, 'listings', adId);
  const adSnap = await getDoc(adRef);

  if (!adSnap.exists()) {
    throw new Error('Ad not found');
  }

  const data = adSnap.data();
  const currentValidTill = data.validTill?.toDate ? data.validTill.toDate() : new Date();
  const newValidTill = new Date(Date.now() + additionalDays * 24 * 60 * 60 * 1000);

  const renewalRecord = {
    renewedAt: Timestamp.fromDate(new Date()),
    renewedBy: userId,
    previousValidTill: Timestamp.fromDate(currentValidTill),
    newValidTill: Timestamp.fromDate(newValidTill),
    renewalType: isPaidRenewal ? 'paid' : 'free',
  };

  await updateDoc(adRef, {
    validTill: Timestamp.fromDate(newValidTill),
    renewalCount: increment(1),
    lastRenewedAt: serverTimestamp(),
    renewalHistory: arrayUnion(renewalRecord),
    status: 'active',
    updatedAt: serverTimestamp(),
  });

  await createNotification({
    userId: userId,
    type: 'ad_posted', // using 'ad_posted' icon for renewal success
    title: 'Ad Republished',
    message: `Your ad "${data.title}" has been republished and is active again.`,
    adId: adId,
    adTitle: data.title
  });

  return newValidTill;
};

// Old Logic deferred for now, but keeping for reference until we are sure the new one works well

export const featureTheAdOld = async (userId, adId) => {
  const userRef = doc(db, "users", userId);
  const adRef = doc(db, "listings", adId);

  try {
    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) throw "User not found";

      const credits = userDoc.data().benefitsOffered?.freeFeaturedAd?.creditsAvailable || 0;
      if (credits <= 0) throw "No credits available";

      // 1. Calculate 7-day expiry
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);

      // 2. Deduct Credit
      transaction.update(userRef, {
        "benefitsOffered.freeFeaturedAd.creditsAvailable": increment(-1),
        "benefitsOffered.freeFeaturedAd.creditsUsed": increment(1),
        updatedAt: serverTimestamp(),
      });

      // 3. Mark Ad as Featured
      transaction.update(adRef, {
        isFeatured: true,
        featuredAt: serverTimestamp(),
        featuredUntil: Timestamp.fromDate(expiryDate),
      });
    });
    return { success: true, message: "Ad featured successfully for 7 days." };
  } catch (error) {
    console.error('Feature Ad Error:', error);
    return { success: false, error: error };
  }
}

//New logic as per changes in exclusive offer pattern. Boost ads instead of featured ads are issued.

/**
 * Deducts 1 "featured" credit from the user and features the specified ad for 7 days.
 */
export const featureTheAd = async (userId, adId) => {
  const userRef = doc(db, "users", userId);
  const adRef = doc(db, "listings", adId);

  const adSnap = await getDoc(adRef);
  if (!adSnap.exists()) throw new Error("Ad not found");

  const ledgerRef = doc(collection(userRef, "ledger")); // Auto-ID subcollection ref

  try {
    await runTransaction(db, async (transaction) => {
      // --- ALL READS FIRST ---
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) throw new Error("User not found");

      const availableCredits = userDoc.data().credits?.featured?.available || 0;
      if (availableCredits <= 0) throw new Error("No featured credits available");

      // --- CALCULATIONS ---
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);

      // --- ALL WRITES AFTER ---

      // 1. Deduct Credit
      transaction.update(userRef, {
        "credits.featured.available": increment(-1),
        "credits.featured.totalUsed": increment(1),
        "credits.featured.lastUsedAt": serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 2. Mark Ad as Featured
      transaction.update(adRef, {
        isFeatured: true,
        featuredAt: serverTimestamp(),
        featuredUntil: Timestamp.fromDate(expiryDate),
      });

      // 3. Write Deduction Ledger Record
      transaction.set(ledgerRef, {
        type: "deduction",
        creditType: "featured",
        amount: -1,
        source: "ad_feature_execution",
        referenceId: adId,
        createdAt: serverTimestamp()
      });

      // 4. Create Notification
      await createNotification({
        userId: userId,
        type: 'ad_posted', // using 'ad_posted' icon for success
        title: 'Ad Featured Successfully',
        message: `Your ad "${adSnap.data().title}" has been featured for 7 days.`,
        adId: adId,
        adTitle: adSnap.data().title,
        transaction: transaction
      });
    });

    return { success: true, message: "Ad featured successfully for 7 days." };
  } catch (error) {
    console.error('Feature Ad Error:', error);
    return { success: false, error: error.message || error };
  }
};

/**
 * Deducts 1 "boost" credit and bumps the ad to the top of the feed by updating activeAt.
 */
export const boostTheAd = async (userId, adId) => {
  const userRef = doc(db, "users", userId);
  const adRef = doc(db, "listings", adId);
  const ledgerRef = doc(collection(userRef, "ledger")); // Auto-ID subcollection ref

  try {
    await runTransaction(db, async (transaction) => {
      // --- ALL READS FIRST ---
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) throw new Error("User not found");

      const adSnap = await transaction.get(adRef);
      if (!adSnap.exists()) throw new Error("Ad not found");
      const adTitle = adSnap.data().title;

      const availableCredits = userDoc.data().credits?.boosts?.available || 0;
      if (availableCredits <= 0) throw new Error("No boost credits available");

      // --- ALL WRITES AFTER ---

      // 1. Deduct Credit
      transaction.update(userRef, {
        "credits.boosts.available": increment(-1),
        "credits.boosts.totalUsed": increment(1),
        "credits.boosts.lastUsedAt": serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 2. Boost the Ad (Updates activeAt; assumes feed queries sort by activeAt DESC)
      transaction.update(adRef, {
        activeAt: serverTimestamp(),
        boostCount: increment(1)
      });

      // 3. Write Deduction Ledger Record
      transaction.set(ledgerRef, {
        type: "deduction",
        creditType: "boosts",
        amount: -1,
        source: "ad_boost_execution",
        referenceId: adId,
        createdAt: serverTimestamp()
      });

      // 4. Create Notification
      await createNotification({
        userId: userId,
        type: 'ad_boosted',
        title: 'Ad Boosted Successfully',
        message: `Your ad "${adTitle}" has been boosted to the top of the local feed.`,
        adId: adId,
        adTitle: adTitle,
        transaction: transaction
      });
    });

    return { success: true, message: "Ad boosted to the top of the feed!" };
  } catch (error) {
    console.error('Boost Ad Error:', error);
    return { success: false, error: error.message || error };
  }
};